import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";
import OliveBranch from "../components/OliveBranch";

const DESIGNS = [
  { id: "classic", name: "Classico", img: "/vouchers/classic.jpg", numColor: "#9C7A2E", pos: { top: "75%", left: "47%" } },
  { id: "festive", name: "Festa", img: "/vouchers/festive.jpg", numColor: "#E4B95B", pos: { top: "76%", left: "47%" } },
  { id: "elegant", name: "Eleganza", img: "/vouchers/elegant.jpg", numColor: "#D8B25E", pos: { top: "68.5%", left: "44%" } },
];

const VoucherPreview = ({ design, amount }) => (
  <div
    className="relative w-full aspect-[3/2] rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
    data-testid={`voucher-preview-${design.id}`}
  >
    <img src={design.img} alt={`Gutschein ${design.name}`} className="absolute inset-0 w-full h-full object-cover" />
    {amount ? (
      <span
        className="absolute font-cormorant font-bold leading-none text-xl sm:text-3xl"
        style={{
          top: design.pos.top,
          left: design.pos.left,
          transform: "translate(-50%, -50%)",
          color: design.numColor,
        }}
      >
        {amount}
      </span>
    ) : null}
  </div>
);

export default function VouchersPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const v = t.vouchers;
  const amounts = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

  const [design, setDesign] = useState("classic");
  const [amount, setAmount] = useState(30);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", recipient_name: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => window.scrollTo(0, 0), []);

  const effectiveAmount = custom ? parseInt(custom, 10) : amount;
  const selectedDesign = DESIGNS.find((d) => d.id === design);
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const pickAmount = (a) => {
    setAmount(a);
    setCustom("");
  };

  const handleBuy = async (e) => {
    e.preventDefault();
    const amt = effectiveAmount;
    if (!amt || amt < 10 || amt > 2000) {
      toast.error(v.errorAmount);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/vouchers/checkout", {
        design,
        amount: amt,
        buyer_name: form.buyer_name,
        buyer_email: form.buyer_email,
        recipient_name: form.recipient_name,
        message: form.message,
        origin_url: window.location.origin,
      });
      // Stripe Checkout kann nicht im iFrame (Preview) geladen werden -> Top-Fenster / neuer Tab
      const url = data.checkout_url;
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
        } else {
          window.location.href = url;
        }
      } catch (e) {
        const w = window.open(url, "_blank");
        if (!w) window.location.href = url;
        setLoading(false);
      }
    } catch (err) {
      toast.error(v.errorGeneric);
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-qs-bg border border-qs-border rounded-lg px-4 py-3 font-outfit text-qs-text placeholder:text-qs-text2/60 focus:outline-none focus:border-qs-gold transition-colors";

  return (
    <div className="bg-qs-bg min-h-screen">
      <section className="relative pt-36 pb-16 px-5 md:px-10 text-center">
        <p className="font-outfit text-qs-gold tracking-[0.35em] uppercase text-xs mb-4">{v.eyebrow}</p>
        <h1 className="font-cormorant text-qs-text text-4xl sm:text-5xl lg:text-6xl">{v.title}</h1>
        <OliveBranch className="w-28 text-qs-gold my-5 mx-auto" />
        <p className="font-outfit text-qs-text2 max-w-xl mx-auto">{v.subtitle}</p>
      </section>

      <form onSubmit={handleBuy} data-testid="voucher-form" className="max-w-6xl mx-auto px-5 md:px-10 pb-24 grid lg:grid-cols-2 gap-12">
        {/* Left: live preview + design */}
        <div>
          <Reveal>
            <VoucherPreview design={selectedDesign} amount={effectiveAmount} />
          </Reveal>
          <p className="font-outfit text-qs-red tracking-[0.25em] uppercase text-xs mt-8 mb-3">{v.chooseDesign}</p>
          <div className="grid grid-cols-3 gap-3">
            {DESIGNS.map((d) => (
              <button
                type="button"
                key={d.id}
                onClick={() => setDesign(d.id)}
                data-testid={`voucher-design-${d.id}`}
                className={`rounded-xl overflow-hidden border-2 transition-all text-left ${
                  design === d.id ? "border-qs-gold scale-[1.02]" : "border-transparent opacity-75 hover:opacity-100"
                }`}
              >
                <div className="aspect-[3/2] w-full overflow-hidden">
                  <img src={d.img} alt={d.name} className="w-full h-full object-cover" />
                </div>
                <div className="bg-qs-bg2 px-2 py-1.5">
                  <p className="font-cormorant text-qs-text text-sm leading-none">{d.name}</p>
                  <p className="font-outfit text-qs-text2 text-[10px]">{v.designDesc[d.id]}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: amount + details */}
        <div>
          <p className="font-outfit text-qs-red tracking-[0.25em] uppercase text-xs mb-3">{v.chooseAmount}</p>
          <div className="grid grid-cols-5 gap-2.5">
            {amounts.map((a) => (
              <button
                type="button"
                key={a}
                onClick={() => pickAmount(a)}
                data-testid={`voucher-amount-${a}`}
                className={`rounded-full py-2.5 font-outfit text-sm font-semibold transition-colors ${
                  !custom && amount === a
                    ? "bg-qs-red text-white"
                    : "bg-qs-bg2 text-qs-text border border-qs-border hover:border-qs-gold"
                }`}
              >
                {a}€
              </button>
            ))}
          </div>
          <div className="mt-4">
            <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{v.custom}</label>
            <input
              type="number"
              min="10"
              max="2000"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder={v.customPh}
              data-testid="voucher-custom-amount"
              className={inputCls}
            />
            <p className="font-outfit text-qs-text2/70 text-xs mt-1.5">{v.customHint}</p>
          </div>

          <p className="font-outfit text-qs-red tracking-[0.25em] uppercase text-xs mt-8 mb-3">{v.yourDetails}</p>
          <div className="space-y-3">
            <input required value={form.buyer_name} onChange={update("buyer_name")} placeholder={v.buyerName} data-testid="voucher-buyer-name" className={inputCls} />
            <input required type="email" value={form.buyer_email} onChange={update("buyer_email")} placeholder={v.buyerEmail} data-testid="voucher-buyer-email" className={inputCls} />
            <input value={form.recipient_name} onChange={update("recipient_name")} placeholder={v.recipient} data-testid="voucher-recipient" className={inputCls} />
            <textarea rows={2} value={form.message} onChange={update("message")} placeholder={v.messagePh} data-testid="voucher-message" className={inputCls} />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="voucher-pay-button"
            className="mt-6 w-full flex items-center justify-center gap-2 bg-qs-gold text-black hover:bg-qs-gold/90 transition-colors rounded-full py-4 text-sm font-semibold tracking-[0.1em] uppercase disabled:opacity-70"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
            {loading ? v.paying : `${v.pay} · ${effectiveAmount || 0}€`}
          </button>
          <p className="flex items-center justify-center gap-1.5 font-outfit text-qs-text2/70 text-xs mt-3">
            <Lock size={11} /> {v.securePay}
          </p>
        </div>
      </form>

      <div className="flex justify-center pb-20">
        <button
          onClick={() => navigate("/")}
          data-testid="vouchers-back-btn"
          className="inline-flex items-center gap-2 border border-qs-border text-qs-text rounded-full px-8 py-4 text-xs font-semibold tracking-[0.14em] uppercase hover:border-qs-gold transition-colors"
        >
          <ArrowLeft size={16} /> {v.back}
        </button>
      </div>
    </div>
  );
}
